import { cn } from "@repo/ui/lib/utils";
import { Link as LinkIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTimeline } from "./context";
import { timelineGaps, timelinePosition } from "./layout";
import { scale } from "./scale";
import { StackedStep, TiledStep } from "./steps";
import { Timeline } from "./track";

interface LayoutProps {
  title?: ReactNode;
  /** Replaces the default "N min timed · N min unallocated" line. */
  summary?: ReactNode;
  headerActions?: ReactNode;
}

/**
 * What the two layouts share: steps sit at their AUTHORED OFFSET on one linear
 * axis, and the complement of those offsets is drawn as unallocated time.
 * Stacking them in flow order instead put a step with a gap before it at the
 * wrong height, and hid the gap entirely.
 */
function usePlacement() {
  const {
    state: { steps, envelopeMinutes },
    meta: { density, at },
  } = useTimeline();
  const gaps = timelineGaps(steps, envelopeMinutes);
  const unallocatedMinutes = gaps.reduce((sum, gap) => sum + gap.costMinutes, 0);
  const extent = Math.max(envelopeMinutes, ...steps.map((step) => step.startMinutes + step.costMinutes));
  return { steps, gaps, unallocatedMinutes, timedMinutes: envelopeMinutes - unallocatedMinutes, extent, density, at };
}

function DefaultSummary({ timed, unallocated }: { timed: number; unallocated: number }) {
  return (
    <>
      {timed} min timed
      {unallocated > 0 && ` · ${unallocated} min unallocated envelope time`}
    </>
  );
}

const ITEM = "absolute inset-x-0 min-w-0 flex-none gap-0 group-data-[orientation=vertical]/timeline:not-last:pb-0";

/**
 * The full-width layout: a card, a wall-clock gutter down the left, dots on the
 * spine, and concurrent procedures tiled side by side.
 */
export function TimelinePageLayout({ title = "Visit timeline", summary, headerActions }: LayoutProps) {
  const { steps, gaps, unallocatedMinutes, timedMinutes, extent, density, at } = usePlacement();
  const gutter = "-left-20 absolute w-12 text-right";
  const inset = "group-data-[orientation=vertical]/timeline:ms-20";

  return (
    <Timeline.Frame className="overflow-hidden rounded-lg border">
      <Timeline.Header className="border-b bg-muted/30 px-4 py-3">
        <Timeline.Title className="text-sm">{title}</Timeline.Title>
        <div className="flex items-center gap-3">
          <Timeline.Summary className="text-xs">
            {summary ?? <DefaultSummary timed={timedMinutes} unallocated={unallocatedMinutes} />}
          </Timeline.Summary>
          {headerActions}
        </div>
      </Timeline.Header>
      <Timeline.Track className="p-4" trackStyle={{ height: scale(extent, density) + 24 }}>
        {steps.map((step, index) => (
          <Timeline.Step
            key={step.id}
            index={index + 1}
            render={<section />}
            className={cn(ITEM, inset)}
            style={timelinePosition(step.startMinutes, step.costMinutes, density)}
            aria-label={`Step ${index + 1} starts at ${at(step.startMinutes)}`}
          >
            <Timeline.Gutter className={gutter}>{at(step.startMinutes)}</Timeline.Gutter>
            <Timeline.Marker className="border-2 border-primary bg-background" />
            <Timeline.Content>
              <TiledStep step={step} />
            </Timeline.Content>
          </Timeline.Step>
        ))}

        {/* The envelope is what gets booked, so the timeline runs to its end, not to the last procedure. */}
        {gaps.map((gap, index) => (
          <Timeline.Step
            key={gap.startMinutes}
            index={steps.length + index + 1}
            render={<section />}
            className={cn(ITEM, inset)}
            style={timelinePosition(gap.startMinutes, gap.costMinutes, density)}
            aria-label={`Unallocated envelope time starts at ${at(gap.startMinutes)}`}
          >
            <Timeline.Gutter className={gutter}>{at(gap.startMinutes)}</Timeline.Gutter>
            <Timeline.Marker className="border border-muted-foreground bg-background" />
            <Timeline.Content>
              <Timeline.Unallocated minutes={gap.costMinutes} />
            </Timeline.Content>
          </Timeline.Step>
        ))}

        <Timeline.Step
          index={steps.length + gaps.length + 1}
          className={cn("absolute inset-x-0 flex-none", inset)}
          style={{ top: scale(extent, density) }}
        >
          <Timeline.Gutter className={gutter}>{at(extent)}</Timeline.Gutter>
          <Timeline.EndMarker className="border border-muted-foreground" />
          <Timeline.Content className="text-xs">
            {unallocatedMinutes > 0 ? "End of visit" : "End of timed work"}
          </Timeline.Content>
        </Timeline.Step>
      </Timeline.Track>
    </Timeline.Frame>
  );
}

/**
 * The narrow-column layout, for the schedule's detail panel (~250px). It drops
 * the surrounding card, since the panel already frames its own content and a
 * second border around a 250px column only narrows it; it narrows the gutter;
 * and it stacks concurrent procedures instead of tiling them. Authored time
 * supplies a minimum height; normal flow lets every procedure stay readable.
 * Gaps remain explicit and gutter labels retain the actual authored times.
 */
export function TimelinePanelLayout({ title = "Visit timeline", summary, headerActions }: LayoutProps) {
  const { steps, gaps, unallocatedMinutes, timedMinutes, extent, density, at } = usePlacement();
  const gutter = "-left-16 absolute w-12 whitespace-nowrap text-right text-[10px]";
  const inset = "group-data-[orientation=vertical]/timeline:ms-16";

  const entries = [
    ...steps.map((step, index) => ({ ...step, step, index })),
    ...gaps.map((gap, index) => ({ ...gap, step: undefined, index: steps.length + index })),
  ].sort((left, right) => left.startMinutes - right.startMinutes);
  const item = cn("relative min-w-0 flex-none gap-0 group-data-[orientation=vertical]/timeline:not-last:pb-0", inset);

  return (
    <Timeline.Frame>
      <Timeline.Header className="pb-2">
        <Timeline.Title className="text-muted-foreground text-xs">{title}</Timeline.Title>
        <div className="flex items-center gap-3">
          <Timeline.Summary className="text-[10px]">
            {summary ?? <DefaultSummary timed={timedMinutes} unallocated={unallocatedMinutes} />}
          </Timeline.Summary>
          {headerActions}
        </div>
      </Timeline.Header>
      <Timeline.Track>
        {entries.map(({ step, index, startMinutes, costMinutes }) => (
          <Timeline.Step
            key={step?.id ?? `gap:${startMinutes}`}
            index={index + 1}
            render={<section />}
            className={item}
            style={{ minHeight: scale(costMinutes, density) }}
            aria-label={
              step
                ? `Step ${index + 1} starts at ${at(startMinutes)}`
                : `Unallocated envelope time starts at ${at(startMinutes)}`
            }
          >
            <Timeline.Gutter className={gutter}>{at(startMinutes)}</Timeline.Gutter>
            {step && step.procedures.length > 1 && (
              <span className="-left-5 absolute top-4 text-muted-foreground" title="Concurrent procedures">
                <LinkIcon aria-label="Concurrent procedures" className="size-2.5" />
              </span>
            )}
            <Timeline.Content>
              {step ? <StackedStep step={step} /> : <Timeline.Unallocated minutes={costMinutes} />}
            </Timeline.Content>
          </Timeline.Step>
        ))}

        <Timeline.Step index={steps.length + gaps.length + 1} className={item}>
          <Timeline.Gutter className={gutter}>{at(extent)}</Timeline.Gutter>
          <Timeline.Content className="text-xs">
            {unallocatedMinutes > 0 ? "End of visit" : "End of timed work"}
          </Timeline.Content>
        </Timeline.Step>
      </Timeline.Track>
    </Timeline.Frame>
  );
}
